import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import {
  compose,
  withAccountHeader,
  withAuthHeader,
  withConfiguredBaseUrl,
  withE2ERequestStubbing,
  withRetries,
} from '../hooks/useService';
import { ServiceAuth } from '../hooks/useService/useService';
import urls from '../util/urls';

export enum IngestionType {
  FOUNDATION = 'Foundation',
  CARIS = 'Caris',
  FOUNDATION_BAM = 'FoundationBam',
  CARIS_BAM = 'CarisBam',
  NEXTGEN = 'NextGen',
  VCF = 'Vcf',
  NEBULA = 'Nebula',
  BROAD = 'Broad',
}

export interface IngestionFileActionNotificationConfig {
  succeededEmail?: string;
  failedEmail?: string;
}

export interface IngestionFileAction {
  ingestionType: IngestionType;
  name: string;
  inputFolder: string;
  timeout: number;
  notificationConfig: IngestionFileActionNotificationConfig;
}

export enum IngestionStep {
  AWAITING_FILES = 'AwaitingFiles',
  SUBMITTED = 'Submitted',
  TRANSFORMED = 'Transformed',
  NORMALIZED = 'Normalized',
  TEST_CREATED = 'TestCreated',
  TEST_NOT_CREATED = 'TestNotCreated',
}

export enum FoundationInputFile {
  XML = 'xml',
  VCF = 'vcf',
  REPORT = 'report',
}

export enum CarisInputFile {
  TAR = 'tar',
}

export enum BamInputFile {
  BAM = 'bam',
}

export enum NextGenInputFile {
  TAR = 'tar',
}

export enum VcfInputFile {
  VCF = 'vcf',
  Manifest = 'manifest',
}
export enum BroadInputFile {
  VCF = 'vcf',
  Manifest = 'manifest',
}

export interface FoundationInputFiles {
  [FoundationInputFile.XML]: string;
  [FoundationInputFile.VCF]: string | null;
  [FoundationInputFile.REPORT]: string;
}

export interface CarisInputFiles {
  [CarisInputFile.TAR]: string;
}

export interface NextGenInputFiles {
  [NextGenInputFile.TAR]: string;
}

export interface BamInputFiles {
  [BamInputFile.BAM]: string;
}

export interface VcfInputFiles {
  [VcfInputFile.VCF]: string;
  [VcfInputFile.Manifest]: string;
}

export interface FoundationIngestionRequest {
  ingestionType: IngestionType.FOUNDATION;
  inputFiles: FoundationInputFiles;
}

export interface CarisIngestionRequest {
  ingestionType: IngestionType.CARIS;
  inputFiles: CarisInputFiles;
}

export interface NextGenIngestionRequest {
  ingestionType: IngestionType.NEXTGEN;
  inputFiles: NextGenInputFiles;
}

export interface BamIngestionRequest {
  ingestionType: IngestionType.FOUNDATION_BAM | IngestionType.CARIS_BAM;
  inputFiles: BamInputFiles;
}

export interface VcfIngestionRequest {
  ingestionType: IngestionType.VCF;
  inputFiles: VcfInputFiles;
}

export interface BroadIngestionRequest {
  ingestionType: IngestionType.BROAD;
  inputFiles: VcfInputFiles;
}

export interface NebulaIngestionRequest {
  ingestionType: IngestionType.NEBULA;
  patientId: string;
  inputFiles: {
    vcf: string;
  };
}

export interface NotificationConfig {
  succeededEmail?: string;
  failedEmail?: string;
}

export type IngestionRequest = (
  | FoundationIngestionRequest
  | CarisIngestionRequest
  | BamIngestionRequest
  | NextGenIngestionRequest
  | VcfIngestionRequest
  | BroadIngestionRequest
  | NebulaIngestionRequest
) & {
  notificationConfig: NotificationConfig;
};

export type ListIngestionsResponse = {
  items: Ingestion[];
  links: {
    self?: string;
    next?: string;
  };
};

export interface Ingestion {
  id: string;
  caseId: string;
  step: IngestionStep;
  ingestionType: IngestionType;
  archive: boolean;
  failed: boolean;
  succeeded: boolean;
  testId?: string;
  patientId?: string;
  createdDate: string;
  endDate?: string;
}

export type IngestionFileActionResource = IngestionFileAction & { id: string };

export type IngestionListParams = {
  name?: string;
  steps?: IngestionStep[];
  failed?: boolean;
  pageSize?: number;
};

export interface IngestionPatientInfo {
  dob?: string;
  firstName?: string;
  lastName?: string;
  mrn?: string;
}

export default class GenomicIngestionService {
  GenomicIngestionAPI: (auth: ServiceAuth) => AxiosInstance;

  constructor(_options?: Partial<AxiosRequestConfig>) {
    this.GenomicIngestionAPI = (auth: ServiceAuth) =>
      compose(
        withE2ERequestStubbing,
        withAccountHeader(auth.account),
        withAuthHeader(auth.authToken),
        withConfiguredBaseUrl(auth.config),
        withRetries(),
      )(axios.create());
  }

  createIngestionFileAction =
    (auth: ServiceAuth) =>
    async (projectId: string, ingestionFileAction: IngestionFileAction) => {
      return this.GenomicIngestionAPI(auth).post(
        urls.api.genomicIngestion.fileAction(projectId),
        ingestionFileAction,
      );
    };

  listIngestionFileActions =
    (auth: ServiceAuth) => async (projectId: string) => {
      return this.GenomicIngestionAPI(auth).get<{
        items: IngestionFileActionResource[];
        links: { self: string };
      }>(urls.api.genomicIngestion.fileAction(projectId));
    };

  updateIngestionFileAction =
    (auth: ServiceAuth) =>
    async (
      projectId: string,
      fileActionId: string,
      ingestionFileAction: IngestionFileAction,
    ) => {
      return this.GenomicIngestionAPI(auth).put<IngestionFileActionResource>(
        urls.api.genomicIngestion.fileAction(projectId, fileActionId),
        ingestionFileAction,
      );
    };

  deleteIngestionFileAction =
    (auth: ServiceAuth) =>
    async (projectId: string, ingestionFileActionId: string) => {
      return this.GenomicIngestionAPI(auth).delete<IngestionFileAction[]>(
        urls.api.genomicIngestion.fileAction(projectId, ingestionFileActionId),
      );
    };

  listIngestions =
    (auth: ServiceAuth) =>
    async (projectId: string, params?: IngestionListParams) => {
      return this.GenomicIngestionAPI(auth).get<ListIngestionsResponse>(
        urls.api.genomicIngestion.ingestions(projectId, undefined, params),
      );
    };

  createIngestion =
    (auth: ServiceAuth) =>
    async (projectId: string, ingestion: IngestionRequest) => {
      return this.GenomicIngestionAPI(auth).post<Ingestion>(
        urls.api.genomicIngestion.ingestions(projectId),
        ingestion,
      );
    };

  triggerReingestion =
    (auth: ServiceAuth) =>
    async (projectId: string, caseId: string, ingestionType: IngestionType) => {
      return this.GenomicIngestionAPI(auth).post<Ingestion>(
        urls.api.genomicIngestion.reingest(projectId),
        {
          caseId,
          ingestionType,
        },
      );
    };

  triggerReingestionWithDeleteSoftDelete =
    (auth: ServiceAuth) =>
    async (projectId: string, caseId: string, ingestionType: IngestionType) => {
      return this.GenomicIngestionAPI(auth).post<Ingestion>(
        urls.api.genomicIngestion.reingest(projectId, {
          deleteSoftDelete: true,
        }),
        {
          caseId,
          ingestionType,
        },
      );
    };

  getIngestionPatientInfo =
    (auth: ServiceAuth) => async (projectId: string, ingestionId: string) => {
      return this.GenomicIngestionAPI(auth).get<{
        patientInfo: IngestionPatientInfo;
      }>(
        urls.api.genomicIngestion.ingestionPatientInfo(projectId, ingestionId),
      );
    };

  updateIngestionPatientInfo =
    (auth: ServiceAuth) =>
    async (
      projectId: string,
      ingestionId: string,
      patientInfo: IngestionPatientInfo,
    ) => {
      return this.GenomicIngestionAPI(auth).put(
        urls.api.genomicIngestion.ingestionPatientInfo(projectId, ingestionId),
        patientInfo,
      );
    };

  getGermlineIngestions =
    (auth: ServiceAuth) =>
    async (projectId: string, germlineIngestionId: string) => {
      return this.GenomicIngestionAPI(auth).get(
        urls.api.genomicIngestion.germlineIngestions(
          projectId,
          germlineIngestionId,
        ),
      );
    };
}
